'use client';
import { IconProp } from '@benzinga/core-ui';
import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { faSoundcloud } from '@fortawesome/free-brands-svg-icons/faSoundcloud';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faSquareXTwitter } from '@fortawesome/free-brands-svg-icons/faSquareXTwitter';

export interface SocialSite {
  name: string;
  color?: string;
  icon: IconProp;
  link: string;
}
export interface AppStore {
  name: AppStoreNames;
  link: string;
  icon: React.ReactElement;
}

export type AppStoreNames = 'google-play-store' | 'apple-app-store';

export interface SocialSites {
  [siteName: string]: SocialSite;
}
export interface AppStores {
  [siteName: string]: AppStore;
}

export const socialSites: SocialSites = {
  facebook: {
    color: '#3b5998',
    icon: faFacebookSquare,
    link: 'https://web.facebook.com/Benzinga/?_rdc=1&_rdr',
    name: 'facebook',
  },
  instagram: {
    color: '#e1306c',
    icon: faInstagram,
    link: 'https://www.instagram.com/benzinga/?hl=en',
    name: 'instagram',
  },
  linkedin: {
    color: '#0072b1',
    icon: faLinkedin,
    link: 'https://www.linkedin.com/company/benzinga',
    name: 'linkedin',
  },
  soundcloud: {
    color: '#fe5000',
    icon: faSoundcloud,
    link: 'https://soundcloud.com/bztv',
    name: 'soundcloud',
  },
  twitter: {
    color: '#000000',
    icon: faSquareXTwitter,
    link: 'https://twitter.com/benzinga',
    name: 'twitter',
  },
  youtube: {
    color: 'red',
    icon: faYoutube,
    link: 'https://www.youtube.com/benzinga',
    name: 'youtube',
  },
};

const GooglePlayImage = (
  <svg
    clipRule="evenodd"
    fillRule="evenodd"
    height="39px"
    strokeLinejoin="round"
    strokeMiterlimit="2"
    viewBox="0 0 120 36"
    width="130px"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2863.6 530.6c-36.3 0-66.9-12.7-91.1-37.7-24-24.3-37.4-57.8-36.8-92 0-36.5 12.4-67.4 36.8-91.9 24.1-25 54.7-37.7 91-37.7 35.9 0 66.5 12.7 91.1 37.7 24.4 25.3 36.8 56.2 36.8 91.9-.4 36.6-12.8 67.5-36.8 91.9-24.1 25.2-54.7 37.8-91 37.8zm-1080.1 0c-35.5 0-66.3-12.5-91.5-37.2-25-24.6-37.7-55.7-37.7-92.4s12.7-67.8 37.7-92.4c24.7-24.7 55.5-37.2 91.5-37.2a127.533 127.533 0 0151.1 10.6c16.1 6.9 29.2 16.3 38.9 27.8l2.4 2.9-27.1 26.6-2.8-3.3c-15.3-18.2-35.8-27.1-62.9-27.1-24.2 0-45.3 8.6-62.7 25.6-17.5 17.1-26.4 39.5-26.4 66.6 0 27.1 8.9 49.5 26.4 66.6 17.4 17 38.5 25.6 62.7 25.6 25.8 0 47.5-8.6 64.4-25.6 10-10 16.2-24 18.4-41.7H1779v-37.4h124.2l.5 3.4c.9 6.3 1.8 12.8 1.8 18.8 0 34.5-10.4 62.4-31 83-23.4 24.4-54 36.8-91 36.8zm1436.1-5.3h-38.3L3064 337.6l1 33.8v153.8h-38.3V276.7h43.7l1.2 1.9 110.3 176.8-1-33.7v-145h38.7v248.6zm-643.8 0H2537V314.1h-67.3v-37.4H2643v37.4h-67.3v211.2h.1zm-137.7 0h-38.7V276.7h38.7v248.6zm-217.5 0h-38.7V314.1h-67.3v-37.4h173.3v37.4h-67.3v211.2zm-130.5-.4h-148.4V276.7h148.4v37.4h-109.6v68.2h98.9v37h-98.9v68.2h109.6v37.4zm710.8-57.7c17.3 17.3 38.3 26 62.7 26 25.1 0 45.6-8.5 62.7-26 17-17 25.6-39.3 25.6-66.2 0-26.9-8.6-49.3-25.5-66.2-17.3-17.3-38.4-26-62.7-26-25.1 0-45.6 8.5-62.6 26-17 17-25.6 39.3-25.6 66.2 0 26.9 8.5 49.3 25.4 66.2zM2732.1 872.4c-94.5 0-171.1 71.7-171.1 170.6 0 98 77.1 170.6 171.1 170.6 94.5 0 171.1-72.2 171.1-170.6 0-98.9-76.6-170.6-171.1-170.6zm0 273.6c-51.7 0-96.2-42.8-96.2-103.4 0-61.5 44.6-103.4 96.2-103.4 51.7 0 96.2 41.9 96.2 103.4.1 61-44.5 103.4-96.2 103.4zm-373.3-273.6c-94.5 0-171.1 71.7-171.1 170.6 0 98 77.1 170.6 171.1 170.6 94.5 0 171.1-72.2 171.1-170.6 0-98.9-76.7-170.6-171.1-170.6zm0 273.6c-51.7 0-96.2-42.8-96.2-103.4 0-61.5 44.6-103.4 96.2-103.4 51.7 0 96.2 41.9 96.2 103.4 0 61-44.5 103.4-96.2 103.4zm-444.2-221.5v72.2h173.3c-5.3 40.5-18.7 70.4-39.2 90.9-25.4 25.4-64.6 53-133.7 53-106.5 0-189.8-86-189.8-192.5s83.3-192.5 189.8-192.5c57.5 0 99.4 22.7 130.5 51.7l51.2-51.2c-43.2-41.4-100.7-73.1-181.3-73.1-146.1 0-268.7 119-268.7 264.7 0 146.1 122.5 264.7 268.7 264.7 78.9 0 138.1-25.8 184.9-74.4 47.7-47.7 62.8-115 62.8-169.3 0-16.9-1.3-32.1-4-45h-244.6c.1-.1.1.8.1.8zm1816.9 56.2C3717.2 942.4 3674 872 3585.4 872c-87.8 0-160.8 69.1-160.8 170.6 0 95.8 72.2 170.6 169.3 170.6 78 0 123.4-47.7 142.1-75.7l-57.9-38.8c-19.2 28.5-45.9 47.2-83.8 47.2-38.3 0-65.1-17.4-82.9-51.7l228.1-94.5c0 .1-8-19-8-19zm-232.6 57c-1.8-65.9 51.2-99.4 89.1-99.4 29.9 0 54.8 14.7 63.3 36.1l-152.4 63.3zM3313.6 1203h74.9V701.8h-74.9V1203zm-123-292.7h-2.7c-16.9-20.1-49-38.3-90-38.3-85.1 0-163.5 74.9-163.5 171.1 0 95.8 78 169.8 163.5 169.8 40.5 0 73.1-18.3 90-38.8h2.7v24.5c0 65.1-34.8 100.2-90.9 100.2-45.9 0-74.4-33-86-60.6l-65.1 27.2c18.7 45 68.6 100.7 151 100.7 87.8 0 162.2-51.7 162.2-177.8V882.2H3191v28.1h-.4zm-86 235.7c-51.7 0-94.9-43.2-94.9-102.9 0-60.2 43.2-103.8 94.9-103.8 51.2 0 90.9 44.1 90.9 103.8.5 59.7-39.6 102.9-90.9 102.9zm977.6-444.2h-179.1V1203h74.9v-189.8h104.3c82.9 0 164.4-60.1 164.4-155.5s-81.2-155.9-164.5-155.9zm2.2 241.4h-106.5v-172h106.5c56.1 0 87.8 46.3 87.8 86 0 39.3-32.1 86-87.8 86zm462.5-71.7c-54.4 0-110.5 24.1-133.7 76.6l66.4 27.6c14.3-27.6 40.5-37 68.2-37 38.8 0 78 23.2 78.9 64.6v5.3c-13.4-7.6-42.8-19.2-78-19.2-71.7 0-144.4 39.2-144.4 112.7 0 67.3 58.8 110.5 124.3 110.5 50.3 0 78-22.7 95.3-49h2.7v38.8h72.2v-192c0-89.4-66.4-138.9-151.9-138.9zm-9.4 274.5c-24.5 0-58.8-12-58.8-42.8 0-38.8 42.8-53.5 79.3-53.5 33 0 48.6 7.1 68.2 16.9-5.4 45-43.4 79-88.7 79.4zm424.7-263.8l-86 217.4h-2.7l-89.1-217.4h-80.6l133.7 303.9-76.2 168.9h78L5045 882.2h-82.8zM4288 1203h74.9V701.8H4288V1203zM418.4 302.1c-11.6 12.5-18.3 31.6-18.3 56.6v886.7c0 25 6.7 44.1 18.7 56.1l3.1 2.7 496.8-496.8v-11.1L421.5 299.4l-3.1 2.7z"
      fill="#0C2140"
      fillRule="nonzero"
      transform="scale(.02208)"
    ></path>
    <path
      d="M1084 973.5L918.3 807.8v-11.6L1084 630.5l3.6 2.2 196 111.4c56.1 31.6 56.1 83.8 0 115.8l-196 111.4-3.6 2.2z"
      fill="#0C2140"
      fillRule="nonzero"
      transform="scale(.02208)"
    ></path>
    <path
      d="M1087.6 971.3L918.3 802l-499.9 499.9c18.3 19.6 49 21.8 83.3 2.7l585.9-333.3M1087.6 632.7L501.7 299.9c-34.3-19.6-65.1-16.9-83.3 2.7L918.3 802l169.3-169.3z"
      fill="#0C2140"
      fillRule="nonzero"
      transform="scale(.02208)"
    ></path>
  </svg>
);

const AppleStoreImage = (
  <svg
    fill="#0c2140"
    height="43.3px"
    strokeLinejoin="round"
    strokeMiterlimit="2"
    viewBox="0 0 120 40"
    width="130px"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M24.997 19.889a5.15 5.15 0 012.45-4.317 5.27 5.27 0 00-4.15-2.244c-1.746-.183-3.439 1.045-4.329 1.045-.907 0-2.277-1.027-3.753-.997a5.534 5.534 0 00-4.651 2.837c-2.011 3.482-.511 8.6 1.416 11.414.963 1.378 2.09 2.918 3.564 2.863 1.442-.06 1.981-.919 3.722-.919 1.725 0 2.231.919 3.734.885 1.548-.026 2.523-1.385 3.453-2.776a11.366 11.366 0 001.579-3.216 4.98 4.98 0 01-3.035-4.575zm-2.841-8.412a5.066 5.066 0 001.159-3.63 5.153 5.153 0 00-3.335 1.726 4.82 4.82 0 00-1.19 3.495 4.265 4.265 0 003.366-1.591zM42.302 27.14h-4.734l-1.136 3.356h-2.005l4.483-12.418h2.083l4.484 12.418h-2.039l-1.136-3.356zm-4.243-1.549h3.752l-1.85-5.447h-.052l-1.85 5.447zm17.1.379c0 2.813-1.506 4.621-3.778 4.621a3.076 3.076 0 01-2.849-1.584h-.043v4.484h-1.858V21.442h1.799v1.506h.034a3.216 3.216 0 012.883-1.6c2.298 0 3.812 1.816 3.812 4.622zm-1.91 0c0-1.833-.947-3.038-2.393-3.038-1.419 0-2.375 1.23-2.375 3.038 0 1.824.956 3.046 2.375 3.046 1.446 0 2.393-1.197 2.393-3.046zm11.876 0c0 2.813-1.507 4.621-3.779 4.621a3.076 3.076 0 01-2.849-1.584h-.043v4.484h-1.858V21.442h1.799v1.506h.034a3.216 3.216 0 012.883-1.6c2.297 0 3.813 1.816 3.813 4.622zm-1.911 0c0-1.833-.947-3.038-2.393-3.038-1.42 0-2.375 1.23-2.375 3.038 0 1.824.955 3.046 2.375 3.046 1.446 0 2.393-1.197 2.393-3.046zm8.495 1.066c.138 1.232 1.334 2.04 2.969 2.04 1.567 0 2.694-.808 2.694-1.919 0-.964-.68-1.541-2.289-1.936l-1.61-.388c-2.28-.551-3.339-1.617-3.339-3.348 0-2.142 1.867-3.614 4.518-3.614 2.625 0 4.424 1.472 4.484 3.614H77.26c-.112-1.239-1.136-1.987-2.634-1.987s-2.521.757-2.521 1.858c0 .878.654 1.395 2.255 1.79l1.368.336c2.548.603 3.605 1.626 3.605 3.443 0 2.323-1.849 3.778-4.793 3.778-2.753 0-4.613-1.421-4.733-3.667h1.902zM83.346 19.3v2.142h1.722v1.472h-1.722v4.991c0 .776.345 1.137 1.102 1.137.204-.004.408-.018.611-.043v1.463c-.34.063-.686.092-1.032.086-1.833 0-2.548-.689-2.548-2.445v-5.189h-1.316v-1.472h1.316V19.3h1.867zm2.718 6.67c0-2.849 1.678-4.639 4.294-4.639 2.625 0 4.295 1.79 4.295 4.639 0 2.856-1.661 4.638-4.295 4.638s-4.294-1.782-4.294-4.638zm6.695 0c0-1.954-.895-3.108-2.401-3.108s-2.401 1.162-2.401 3.108c0 1.962.895 3.106 2.401 3.106s2.401-1.144 2.401-3.106zm3.426-4.528h1.773v1.541h.043a2.164 2.164 0 012.177-1.635c.214-.001.428.022.637.069v1.738a2.62 2.62 0 00-.835-.112l-.076-.002a1.883 1.883 0 00-1.873 1.873 1.87 1.87 0 00.012.212v5.37h-1.858v-9.054zm13.198 6.395c-.25 1.643-1.85 2.771-3.898 2.771-2.634 0-4.269-1.764-4.269-4.595 0-2.84 1.644-4.682 4.191-4.682 2.505 0 4.08 1.721 4.08 4.466v.637h-6.395v.112l-.009.208a2.37 2.37 0 002.358 2.358l.087-.002a2.05 2.05 0 002.091-1.273h1.764zm-6.282-2.702h4.526l.004-.121a2.19 2.19 0 00-2.177-2.178l-.047.001a2.306 2.306 0 00-2.306 2.292v.006zM37.826 8.731l.189-.007a2.653 2.653 0 012.64 2.64 2.63 2.63 0 01-.021.332c0 1.906-1.03 3.002-2.808 3.002h-2.155V8.731h2.155zm-1.228 5.123h1.125l.112.003a1.884 1.884 0 001.875-1.876 1.79 1.79 0 00-.02-.273c.012-.085.018-.17.018-.256a1.89 1.89 0 00-1.881-1.881l-.104.003h-1.125v4.28zm5.083-1.41a2.143 2.143 0 012.123-2.336c1.17 0 2.133.963 2.133 2.133l-.009.203.01.207c0 1.171-.963 2.134-2.134 2.134s-2.133-.963-2.133-2.134l.01-.207zm3.333 0c0-.976-.439-1.547-1.208-1.547s-1.207.571-1.207 1.547c0 .984.434 1.551 1.207 1.551s1.208-.571 1.208-1.551zm6.559 2.254h-.922l-.93-3.317h-.071l-.926 3.317h-.913l-1.242-4.503h.902l.806 3.436h.067l.926-3.436h.852l.926 3.436h.07l.803-3.436h.889l-1.237 4.503zm2.281-4.503h.855v.715h.066a1.35 1.35 0 011.344-.802l.11-.004a1.472 1.472 0 011.449 1.679v2.915h-.889v-2.692c0-.724-.314-1.084-.972-1.084l-.048-.001a1.04 1.04 0 00-1.033 1.033.98.98 0 00.006.109v2.635h-.888v-4.503zm5.24-1.758h.889v6.261h-.889zm2.124 4.007a2.144 2.144 0 012.124-2.336c1.171 0 2.133.963 2.133 2.133l-.01.203a2.05 2.05 0 01.01.207c0 1.17-.963 2.134-2.133 2.134s-2.134-.964-2.134-2.134l.01-.207zm3.333 0c0-.976-.439-1.547-1.208-1.547s-1.207.571-1.207 1.547c0 .984.434 1.551 1.207 1.551s1.208-.571 1.208-1.551zm1.85.98c0-.81.603-1.278 1.675-1.344l1.219-.07v-.389c0-.475-.314-.744-.921-.744-.497 0-.84.182-.939.5h-.86c.091-.773.818-1.269 1.84-1.269 1.128 0 1.765.562 1.765 1.513v3.077h-.855v-.633h-.071a1.52 1.52 0 01-1.352.707 1.33 1.33 0 01-.141.007c-.744 0-1.357-.61-1.36-1.355zm2.894-.384v-.377l-1.099.07c-.62.042-.902.253-.902.65 0 .405.352.641.835.641a.97.97 0 00.107.005c.556 0 1.022-.435 1.059-.989zm2.053-.596c0-1.423.732-2.324 1.869-2.324a1.486 1.486 0 011.381.79h.067V8.437h.888v6.261h-.851v-.712h-.071a1.565 1.565 0 01-1.414.786c-1.145 0-1.869-.901-1.869-2.328zm.918 0c0 .955.45 1.53 1.203 1.53s1.212-.583 1.212-1.526-.468-1.53-1.212-1.53-1.203.579-1.203 1.526zm6.964 0l-.01-.203c0-1.17.963-2.133 2.134-2.133s2.133.963 2.133 2.133l-.01.203a2.05 2.05 0 01.01.207c0 1.171-.963 2.134-2.133 2.134s-2.134-.963-2.134-2.134l.01-.207zm3.333 0c0-.976-.438-1.547-1.208-1.547s-1.207.571-1.207 1.547c0 .984.435 1.551 1.207 1.551s1.208-.571 1.208-1.551zm2.106-2.249h.856v.715h.066a1.35 1.35 0 011.344-.802l.11-.004a1.47 1.47 0 011.464 1.464c0 .072-.005.144-.015.215v2.915h-.889v-2.692c0-.724-.314-1.084-.972-1.084l-.048-.001a1.04 1.04 0 00-1.033 1.033.98.98 0 00.006.109v2.635h-.889v-4.503zm8.846-1.121v1.141h.976v.749h-.976v2.315c0 .472.194.679.637.679.113-.001.226-.008.339-.021v.74c-.16.029-.322.044-.484.046-.988 0-1.381-.348-1.381-1.216v-2.543h-.715v-.749h.715V9.074h.889zm2.19-.637h.88v2.481h.071c.241-.53.792-.853 1.373-.806l.08-.002a1.49 1.49 0 011.484 1.483c0 .066-.005.132-.013.198v2.907h-.89V12.01c0-.719-.335-1.084-.963-1.084l-.086-.003a1.06 1.06 0 00-1.052 1.052l.004.093v2.63h-.888V8.437zm9.056 5.045a1.833 1.833 0 01-1.951 1.303h-.054a2.055 2.055 0 01-2.026-2.325 2.086 2.086 0 012.058-2.352c1.271 0 2.027.856 2.027 2.27v.31h-3.18v.05a1.1 1.1 0 00-.004.1c0 .653.537 1.19 1.19 1.19a1.09 1.09 0 001.085-.546h.855zm-3.126-1.451h2.275l.003-.081c0-.596-.491-1.086-1.087-1.086h-.025a1.16 1.16 0 00-1.166 1.152v.015z"></path>
  </svg>
);

export const appStores: AppStores = {
  android: {
    icon: GooglePlayImage,
    link: 'https://play.google.com/store/apps/details?id=com.benzinga.app&hl=en_NZ&gl=US',
    name: 'google-play-store',
  },
  ios: {
    icon: AppleStoreImage,
    link: 'https://apps.apple.com/us/app/benzinga-stock-news-tracker/id688949481',
    name: 'apple-app-store',
  },
};

export interface FooterLink {
  label: string;
  url: string;
}

export const popularChannels: FooterLink[] = [
  {
    label: 'PreMarket Playbook',
    url: '/premarket-playbook/',
  },
  {
    label: 'Press Releases',
    url: '/pressreleases',
  },
  {
    label: 'Analyst Ratings',
    url: '/analyst-stock-ratings',
  },
  {
    label: 'News',
    url: '/news',
  },
  {
    label: 'Options',
    url: '/markets/options',
  },
  {
    label: 'ETFs',
    url: '/etfs',
  },
];

export const toolsAndFeatures: FooterLink[] = [
  {
    label: 'Real Time Feed',
    url: 'https://pro.benzinga.com/',
  },
  {
    label: 'Public RSS Feeds',
    url: '/feeds/list',
  },
  {
    label: 'Submit News Tips',
    url: '/contact',
  },
  {
    label: 'Blog',
    url: 'https://pro.benzinga.com/blog/',
  },
  {
    label: 'Embeddable Finance Widgets & Tools',
    url: '/widgets',
  },
  {
    label: 'Benzinga Catalyst',
    url: '/catalyst',
  },
];

export const partnersAndContributors: FooterLink[] = [
  {
    label: 'Affiliate Program',
    url: 'https://app.impact.com/campaign-promo-signup/Benzinga1659111587301.brand?execution=e1s1',
  },
  {
    label: 'Contributor Portal',
    url: 'https://contributor.benzinga.com/contributor-onboarding',
  },
  {
    label: 'Licensing & Syndication',
    url: '/apis/',
  },
  {
    label: 'Sponsored Content',
    url: '/reach/',
  },
  {
    label: 'Advertise With Us',
    url: 'https://advertise.benzinga.com/',
  },
  {
    label: 'Lead Generation & SEO',
    url: '/contact/',
  },
];

export const aboutBenzinga: FooterLink[] = [
  {
    label: 'About Us',
    url: '/about',
  },
  // {
  //   label: 'Team',
  //   url: '#',
  // },
  {
    label: 'Careers',
    url: 'https://jobs.benzinga.com/',
  },
  {
    label: 'In The News',
    url: '/inthenews',
  },
  {
    label: 'Events',
    url: '/events/',
  },
  {
    label: 'Contact Us',
    url: '/contact',
  },
];

export const copyrightSection: FooterLink[] = [
  {
    label: 'Terms & Conditions ',
    url: '/terms-and-conditions',
  },
  {
    label: ' Do Not Sell My Personal Data/Privacy Policy',
    url: '/page/privacy',
  },
  {
    label: 'Disclaimer ',
    url: '/disclaimer',
  },
  {
    label: 'Service Status',
    url: 'http://www.benzingastatus.com',
  },
  {
    label: 'Sitemap',
    url: '/sitemap',
  },
];

export interface FooterStickyNav {
  image: string;
  label: string;
  url: string;
}

export const footerStickyNavigation: FooterStickyNav[] = [
  {
    image: '/next-assets/images/investing-ideas.svg',
    label: 'Investing Ideas',
    url: '/alternative-investments',
  },
  {
    image: '/next-assets/images/money-offers.svg',
    label: 'Money Offers',
    url: '/money/newsletter/',
  },
  {
    image: '/next-assets/images/guides-and-reviews.svg',
    label: 'Guides & Reviews',
    url: '/money/',
  },
  {
    image: '/next-assets/images/account.svg',
    label: 'My Account',
    url: '/profile/portfolio/',
  },
  {
    image: '/next-assets/images/trending.svg',
    label: 'Trending',
    url: '/',
  },
  {
    image: '/next-assets/images/tools.svg',
    label: 'Tools',
    url: '/tools',
  },
  {
    image: '/next-assets/images/learn.svg',
    label: 'Learn',
    url: '/research/trading-school/login',
  },
];

export interface FooterNavigationGroup {
  label: string;
  items: FooterLink[];
}
